import { useQuery } from "@tanstack/react-query";

export type TimeFilter = "day" | "week" | "month" | "year" | "all" | "custom";

export interface FuelTransactionHistoryItem {
    id: string;
    fiatAmount: number;
    fiatAmountFormatted: string;
    fuelAmount: number;
    type: "DEBIT" | "CREDIT";
    subject: string;
    description: string;
    createdAt: string;
}

export interface FuelTransactionHistoryResponse {
    transactions: FuelTransactionHistoryItem[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
    timeFilter: string;
    startDate: string;
    endDate: string;
}

interface UseGetTransactionHistoryFuelParams {
    timeFilter?: TimeFilter;
    enabled?: boolean;
}

export const useGetTransactionHistoryFuel = (
    fullToken: string | undefined,
    params: UseGetTransactionHistoryFuelParams = {},
) => {
    const { timeFilter = "all", enabled = true } = params;

    return useQuery({
        queryKey: ["transactionHistoryFuel-v1", fullToken || "anonymous", timeFilter],
        queryFn: async () => {
            if (!fullToken) {
                throw new Error("No authentication token");
            }

            const params = new URLSearchParams({
                timeFilter,
            });

            const basePath = import.meta.env.VITE_BACKEND_REST_URL;
            if (!basePath) {
                throw new Error("VITE_BACKEND_REST_URL environment variable is not set. Please check your .env file.");
            }
            const response = await fetch(
                `${basePath}/devourplay/transaction-history-fuel?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${fullToken}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        },
        enabled: enabled && !!fullToken,
        staleTime: 1000 * 60 * 2, // Cache for 2 minutes per timeFilter
        gcTime: 1000 * 60 * 10, // Keep in memory for 10 minutes
        refetchOnWindowFocus: false,
        retry: 2,
    });
};